/* MyCardPage — "/my/card". Student's own Clarity Card + parent view + share link. */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Share2, Copy, Users, IdCard, Check, Linkedin } from 'lucide-react';
import Navbar from '../components/Navbar';
import ClarityCard from '../components/clarity/ClarityCard';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://learnixta.com';

const ParentSummary = ({ card }) => {
  const c = card.counts || {};
  const rows = card.parent_rows;
  return (
    <div className="bg-white rounded-3xl border border-gray-200 shadow-sm p-6 md:p-8 max-w-xl mx-auto" data-testid="parent-view-summary">
      <p className="text-xs font-bold uppercase tracking-widest text-teal-600 mb-2">For parents · simple summary</p>
      <h2 className="text-2xl font-black text-gray-900 mb-1">{card.name}</h2>
      <p className="text-sm text-gray-500 mb-5">is building career clarity on LearnixTa</p>
      <div className="space-y-3 text-sm text-gray-800">
        <div className="flex justify-between gap-4 border-b border-gray-100 pb-2">
          <span className="text-gray-500 shrink-0">Chosen direction</span>
          <span className="font-bold text-right">{card.committed_path || 'Still exploring'}</span>
        </div>
        {rows ? (
          rows.map((r) => (
            <div key={r.label} className="flex justify-between gap-4 border-b border-gray-100 pb-2" data-testid={`parent-row-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <span className="text-gray-500 shrink-0">{r.label}</span>
              <span className="font-bold text-right">{r.value}</span>
            </div>
          ))
        ) : (
          <>
            <div className="flex justify-between gap-4 border-b border-gray-100 pb-2">
              <span className="text-gray-500 shrink-0">Career days experienced</span>
              <span className="font-bold text-right">{c.stories_lived ? c.stories_lived : 'Just starting'}</span>
            </div>
            <div className="flex justify-between gap-4 border-b border-gray-100 pb-2">
              <span className="text-gray-500 shrink-0">Life skills started</span>
              <span className="font-bold text-right">{c.skills_started ? c.skills_started : 'Just starting'}</span>
            </div>
          </>
        )}
        <div className="flex justify-between gap-4">
          <span className="text-gray-500 shrink-0">Overall exploration</span>
          <span className="font-bold text-right">{card.progress_pct ?? 0}% of paths touched</span>
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-5 leading-relaxed">
        Every number above comes from real activity — quizzes taken, career days lived, and projects submitted.
        Nothing is self-declared.
      </p>
    </div>
  );
};

export default function MyCardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [err, setErr] = useState('');
  const [view, setView] = useState('card'); // card | parent
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user === false) { navigate('/signin', { replace: true, state: { next: '/my/card' } }); return; }
    if (!user) return;
    axios.get(`${API}/api/clarity-card/me`)
      .then((r) => setCard(r.data))
      .catch(() => setErr('Could not load your Career Clarity Card.'));
  }, [user, navigate]);

  const shareUrl = card ? `${SITE_URL}/card/${card.slug}` : '';
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-teal-50">
      <Navbar />
      <div className="pt-28 pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-xs font-black uppercase tracking-[0.25em] text-teal-600 mb-2 flex items-center justify-center gap-1.5">
              <IdCard size={14} /> My Career Clarity Card
            </p>
            <h1 className="text-3xl md:text-4xl font-black text-gray-900">Your living career identity</h1>
            <p className="text-sm text-gray-500 mt-2">Built automatically from everything you do — quizzes, stories, badges, skills.</p>
          </div>

          {/* View toggle */}
          <div className="flex justify-center gap-2 mb-8">
            <button onClick={() => setView('card')}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${view === 'card' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
              data-testid="mycard-view-card-btn">
              <IdCard size={14} className="inline mr-1.5 -mt-0.5" /> Card view
            </button>
            <button onClick={() => setView('parent')}
              className={`px-4 py-2 rounded-full text-sm font-bold transition-colors ${view === 'parent' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
              data-testid="mycard-view-parent-btn">
              <Users size={14} className="inline mr-1.5 -mt-0.5" /> Parent view
            </button>
          </div>

          {err && <p className="text-center text-sm text-rose-600" data-testid="mycard-error">{err}</p>}
          {!card && !err && (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
          )}

          {card && view === 'card' && <ClarityCard card={card} />}
          {card && view === 'parent' && <ParentSummary card={card} />}

          {/* Share */}
          {card && !card.is_empty && (
            <div className="mt-10 bg-white rounded-2xl border border-gray-200 p-5 max-w-xl mx-auto" data-testid="mycard-share-section">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2 flex items-center gap-1.5">
                <Share2 size={12} /> Public share link
              </p>
              <p className="text-xs text-gray-500 mb-3">Anyone with this link sees only your name, path, fit band and badges — nothing else.</p>
              <div className="flex gap-2">
                <input readOnly value={shareUrl} className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-700 font-mono" data-testid="mycard-share-url" />
                <Button size="sm" onClick={copyLink} data-testid="mycard-copy-link-btn">
                  {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${SITE_URL}/api/clarity-card/og/${card.slug}`)}`}
                target="_blank"
                rel="noreferrer"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#0A66C2] hover:bg-[#084d92] text-white font-extrabold text-sm py-2.5 rounded-full transition-colors"
                data-testid="mycard-linkedin-share-btn"
              >
                <Linkedin size={16} /> Share to LinkedIn
              </a>
              <p className="mt-2 text-[11px] text-gray-400">The LinkedIn post shows your card as a picture — the live flip opens when people click your link.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
